import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoicePicker } from "@/components/VoicePicker";
import {
  SENSITIVITIES, DEFAULT_WAKE,
  getWakeWord, setWakeWord, getWakeSensitivity, setWakeSensitivity,
} from "@/lib/voice";
import { Settings2, Ear, Volume2, RotateCcw } from "lucide-react";

export function WakeSettings({ voice, onVoice, trigger }) {
  const [open, setOpen] = useState(false);
  const [word, setWord] = useState(getWakeWord());
  const [sens, setSens] = useState(getWakeSensitivity());

  const saveWord = (w) => { setWord(w); setWakeWord(w); };
  const saveSens = (s) => { setSens(s); setWakeSensitivity(s); };
  const reset = () => saveWord(DEFAULT_WAKE);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button data-testid="wake-settings-btn" title="Réglages voix & activation"
            className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors">
            <Settings2 size={18} />
          </button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="wake-settings-dialog" className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 size={18} className="text-primary" /> Voix & activation
          </DialogTitle>
          <DialogDescription>Personnalise la voix d'OMY et la manière de le réveiller.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-1">
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Volume2 size={15} className="text-primary" />
              <h3 className="text-sm font-medium">Voix d'OMY</h3>
            </div>
            <VoicePicker value={voice} onChange={onVoice} />
          </section>

          <section>
            <div className="flex items-center gap-2 mb-2">
              <Ear size={15} className="text-primary" />
              <h3 className="text-sm font-medium">Mot d'activation</h3>
            </div>
            <div className="flex items-center gap-2">
              <Input data-testid="wake-word-input" value={word}
                onChange={(e) => saveWord(e.target.value)} placeholder="Hey Omy"
                className="bg-black/30" />
              <button data-testid="wake-word-reset" onClick={reset} title="Réinitialiser"
                className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-muted-foreground hover:text-foreground bg-white/5">
                <RotateCcw size={15} />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              Dis ce mot depuis n'importe quelle page pour réveiller OMY.
            </p>
          </section>

          <section>
            <Label className="text-sm font-medium mb-2 block">Sensibilité du micro</Label>
            <div className="grid grid-cols-3 gap-2">
              {SENSITIVITIES.map((s) => (
                <button key={s.id} data-testid={`wake-sens-${s.id}`} onClick={() => saveSens(s.id)}
                  className={`rounded-xl border px-2 py-2 text-xs font-medium transition-colors ${
                    sens === s.id ? "border-primary/60 bg-primary/10 text-primary" : "border-white/10 text-muted-foreground hover:border-white/25"}`}>
                  {s.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              {SENSITIVITIES.find((s) => s.id === sens)?.desc}
            </p>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
